// Wall/door edge model for a base's cell grid (the `Set<"row:col">` shape
// produced by building-footprint.mjs's generateBaseShape). An edge is the
// boundary shared by two adjacent cells, keyed "v:row:col" (the edge between
// (row,col) and (row,col+1)) or "h:row:col" (between (row,col) and
// (row+1,col)) — the same scheme validated interactively in
// prototype/base-geometry-lab.html.
//
// State is persisted as two arrays of edge keys (walls/doors). Sets are rebuilt
// locally inside each function for O(1) lookups, and every mutator returns a new
// state object without mutating the input.

export function cellKey(row, col) {
  return `${row}:${col}`;
}

export function parseCellKey(key) {
  return key.split(":").map(Number);
}

export function verticalEdgeKey(row, col) {
  return `v:${row}:${col}`;
}

export function horizontalEdgeKey(row, col) {
  return `h:${row}:${col}`;
}

// The two cells an edge key separates, as [row,col] pairs.
export function edgeCells(edgeKey) {
  const [kind, rowText, colText] = edgeKey.split(":");
  const row = Number(rowText);
  const col = Number(colText);
  return kind === "v" ? [[row, col], [row, col + 1]] : [[row, col], [row + 1, col]];
}

// The (up to 4) [neighborRow, neighborCol, edgeKey] triples around a cell.
export function neighborEdges(row, col) {
  return [
    [row, col + 1, verticalEdgeKey(row, col)],
    [row, col - 1, verticalEdgeKey(row, col - 1)],
    [row + 1, col, horizontalEdgeKey(row, col)],
    [row - 1, col, horizontalEdgeKey(row - 1, col)],
  ];
}

export function createWallState() {
  return { wallEdgeKeys: [], doorEdgeKeys: [] };
}

function toSets(state) {
  return {
    walls: new Set(state?.wallEdgeKeys || []),
    doors: new Set(state?.doorEdgeKeys || []),
  };
}

function fromSets(walls, doors) {
  return { wallEdgeKeys: [...walls], doorEdgeKeys: [...doors] };
}

// A wall can only be placed between two cells that are both inside the base.
export function isEdgeBuildable(cells, edgeKey) {
  const [a, b] = edgeCells(edgeKey);
  return cells.has(cellKey(...a)) && cells.has(cellKey(...b));
}

// True when exactly one side of the edge is inside the base — the boundary
// between the base and the outside world.
export function isExteriorEdge(cells, edgeKey) {
  const [a, b] = edgeCells(edgeKey);
  return cells.has(cellKey(...a)) !== cells.has(cellKey(...b));
}

// Every exterior edge key for a cell set (the base's outer wall).
export function exteriorBoundaryEdges(cells) {
  const edges = new Set();
  for (const key of cells) {
    const [row, col] = parseCellKey(key);
    for (const [nr, nc, edgeKey] of neighborEdges(row, col)) {
      if (!cells.has(cellKey(nr, nc))) edges.add(edgeKey);
    }
  }
  return edges;
}

export function addWall(state, cells, edgeKey) {
  if (!isEdgeBuildable(cells, edgeKey)) return state;
  const { walls, doors } = toSets(state);
  if (walls.has(edgeKey)) return state;
  walls.add(edgeKey);
  return fromSets(walls, doors);
}

// Removing a wall also clears any door standing on it — a door with no wall
// either side of it isn't a meaningful state.
export function removeWall(state, edgeKey) {
  const { walls, doors } = toSets(state);
  if (!walls.has(edgeKey) && !doors.has(edgeKey)) return state;
  walls.delete(edgeKey);
  doors.delete(edgeKey);
  return fromSets(walls, doors);
}

// Interior doors require a wall already on that edge (you're cutting a
// doorway into a built wall); exterior doors don't, since the base's outer
// boundary is implicitly walled everywhere it isn't a door.
export function placeDoor(state, cells, edgeKey) {
  const { walls, doors } = toSets(state);
  if (doors.has(edgeKey)) return state;
  const exterior = isExteriorEdge(cells, edgeKey);
  if (!exterior && !walls.has(edgeKey)) return state;
  doors.add(edgeKey);
  return fromSets(walls, doors);
}

export function removeDoor(state, edgeKey) {
  const { walls, doors } = toSets(state);
  if (!doors.has(edgeKey)) return state;
  doors.delete(edgeKey);
  return fromSets(walls, doors);
}

// An edge blocks movement iff it's a wall and not also a door.
export function isPassable(state, edgeKey) {
  const { walls, doors } = toSets(state);
  return !(walls.has(edgeKey) && !doors.has(edgeKey));
}

export function isDoor(state, edgeKey) {
  return new Set(state?.doorEdgeKeys || []).has(edgeKey);
}

export function isWall(state, edgeKey) {
  return new Set(state?.wallEdgeKeys || []).has(edgeKey);
}
